import React, { useContext, useEffect, useState } from 'react';
import Text from 'react-texty';
import 'react-texty/styles.css';

import { CalculateSettings, Debouncer } from './Functions';
import { DeepCopy } from '../../tables/shared/TableEventHelpers';
import { GetUpdatedSettings, GetWindow } from '../requests/CommonRequests';
import EditSelectMany from './field_inputs/select_many/EditSelectMany';
import EditText from './field_inputs/text/EditText';
import WeekHelper from '../../../functions/WeekHelper';
import { DataContext as CrmDataContext } from '../../../stores/crm-store';


export const Header = ({ layout, dataContext }) => {
    return (
        <div className='card d-flex flex-column sticky-element mb-2'>
            <div className='d-flex flex-row justify-content-left align-items-left'>
                {layout.map((field) => {
                    return <HeaderCell key={`header-${field.textContent}`} width={field.width} textContent={field.textContent} />;
                })}
            </div>
            <div className='d-flex flex-row justify-content-left align-items-left'>
                {layout.map((field) => {
                    if (field.disabledFilter) {
                        return <EmptyFilterCell key={`filter-${field.textContent}`} width={field.width} textContent={field.filterValue} />;
                    }
                    if (field.filterType === 'text') {
                        return (
                            <FilterCell
                                key={`filter-${field.textContent}`}
                                width={field.width}
                                textContent={field.filterValue}
                                dataContext={dataContext}
                            />
                        );
                    } else if (field.filterType === 'select') {
                        let descriptionText;
                        if (field.filterValue === 'office') {
                            descriptionText = 'Välj Stad';
                        } else if (field.filterValue === 'role') {
                            descriptionText = 'Välj Roll';
                        } else if (field.filterValue === 'organizationType') {
                            descriptionText = 'Välj Typ';
                        } else if (field.filterValue === 'skill') {
                            descriptionText = 'Välj Kunskap';
                        } else {
                            descriptionText = 'Välj Enhet';
                        }
                        return (
                            <SelectFilter
                                key={`filter-${field.textContent}`}
                                width={field.width}
                                descriptionText={descriptionText}
                                textContent={field.filterValue}
                                dataContext={dataContext}
                            />
                        );
                    } else {
                        return <EmptyFilterCell key={`filter-${field.textContent}`} width={field.width} textContent={field.filterValue} />;
                    }
                })}
            </div>
        </div>
    );
};

export const HeaderCell = ({ textContent, width }) => {
    return (
        <div className='card border padding-top-20px padding-bottom-20px' style={{ width, minWidth: width, maxWidth: width }}>
            <strong
                className='pl-3 ellipsis-text user-select-none margin-none justify-content-center align-content-center'
                style={{ height: 25 }}>
                {textContent}
            </strong>
        </div>
    );
};

export const EmptyFilterCell = ({ textContent, width }) => {
    return (
        <div
            key={textContent + '-empty-filter'}
            className='p-0 border border-bottom-0'
            style={{ width, minWidth: width, maxWidth: width }}
        />
    );
};

export const FilterCell = ({ dataContext, textContent, width }) => {
    const {
        store: { apiUrl, requestObject, viewSettings },
        dispatch,
    } = useContext(dataContext);

    const [fetch, setFetch] = useState(false);
    const [filterValue, setFilterValue] = useState(
        requestObject !== null && requestObject !== undefined && requestObject.filters[textContent] !== undefined
            ? requestObject.filters[textContent]
            : ''
    );
    const debouncedFilter = Debouncer(filterValue, 500);

    const finishedDataGet = (res) => {
        if (res.success) {
            let newViewSettings = DeepCopy(viewSettings);
            newViewSettings.maxIndex = res.totalCount;
            newViewSettings = CalculateSettings(newViewSettings, requestObject);

            dispatch({ type: 'UPDATE_DATA', payload: res.data.dataEntities });
            dispatch({ type: 'UPDATE_VIEW_SETTINGS', viewSettings: newViewSettings });
        }
    };

    useEffect(() => {
        if (requestObject !== null && fetch) {
            setFetch(true);
            let newRequestObject = DeepCopy(requestObject);

            if (
                newRequestObject.filters[textContent] === undefined ||
                newRequestObject.filters[textContent].length !== debouncedFilter.length
            ) {
                if (debouncedFilter.length > 0) newRequestObject.filters[textContent] = debouncedFilter;
                else delete newRequestObject.filters[textContent];

                newRequestObject.startIndex = 0;
                dispatch({ type: 'UPDATE_REQUEST_OBJECT', requestObject: newRequestObject });

                GetWindow(apiUrl, newRequestObject, finishedDataGet);
            }
        } else {
            setFetch(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFilter]);

    return (
        <div className='p-0 border border-bottom-0 d-flex align-items-start' style={{ width, minWidth: width, maxWidth: width }}>
            <div key={textContent + '-filter'} className='m-1 d-flex w-100'>
                <EditText value={filterValue} setValue={setFilterValue} />
            </div>
        </div>
    );
};

export const SelectFilter = ({ dataContext, descriptionText, textContent, width }) => {
    const {
        store: { apiUrl, requestObject, viewSettings },
        dispatch,
    } = useContext(dataContext);

    const { store } = useContext(CrmDataContext);

    const [fetch, setFetch] = useState(false);
    const [filterValue, setFilterValue] = useState(
        requestObject !== null && requestObject !== undefined && requestObject.filters[textContent] !== undefined
            ? requestObject.filters[textContent]
            : ''
    );
    const debouncedFilter = Debouncer(filterValue, 200);

    const finishedDataGet = (res) => {
        if (res.success) {
            let newViewSettings = DeepCopy(viewSettings);
            newViewSettings.maxIndex = res.totalCount;
            newViewSettings = CalculateSettings(newViewSettings, requestObject);

            dispatch({ type: 'UPDATE_DATA', payload: res.data.dataEntities });
            dispatch({ type: 'UPDATE_VIEW_SETTINGS', viewSettings: newViewSettings });
        }
    };

    useEffect(() => {
        if (requestObject !== null && requestObject !== undefined) {
            if (requestObject.filters[textContent] !== undefined) {
                setFilterValue(requestObject.filters[textContent]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestObject]);

    useEffect(() => {
        if (requestObject !== null && fetch) {
            let newRequestObject = DeepCopy(requestObject);

            if (
                newRequestObject.filters[textContent] === undefined ||
                newRequestObject.filters[textContent].length !== debouncedFilter.length
            ) {
                if (debouncedFilter.length > 0) newRequestObject.filters[textContent] = debouncedFilter;
                else delete newRequestObject.filters[textContent];

                newRequestObject.startIndex = 0;
                dispatch({ type: 'UPDATE_REQUEST_OBJECT', requestObject: newRequestObject });

                GetWindow(apiUrl, newRequestObject, finishedDataGet);
            }
        } else {
            setFetch(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFilter]);

    return (
        <div className='p-0 border border-bottom-0' style={{ width, minWidth: width, maxWidth: width }}>
            <div key={textContent + '-filter'} className='m-1 d-flex flex-column align-content-center justify-content-center'>
                <EditSelectMany value={filterValue} setValue={setFilterValue} options={store[textContent + 'Options']} />
            </div>
        </div>
    );
};

export const TableCell = ({ item, height, disabled, type, content, link, width, onClick = null }) => {
    const WEEKS_BEFORE_ALERT = 8; // Amount of weeks before the endDate text turns red

    let text = '';
    let tooltipSpecificText = '';
    let textColor;

    if (item === null) {
        text = '';
    } else if (content.split('.').length > 1) {
        var fields = content.split('.');
        var finalObj = item;
        for (var i = 0; i < fields.length; i++) {
            if (finalObj[fields[i]] !== null) finalObj = finalObj[fields[i]];
        }
        text = finalObj;
    } else if (disabled || item[content] === null || item[content] === undefined) {
        text = '';
    } else if (type === 'skill' || type === 'list') {
        if (item[content]?.length) {
            if (item.activityPersons?.length) {
                text = item[content].map((object) => object.person.name).reduce((prev, next) => '' + prev + ', ' + next);
            }
            else {
                text = item[content].map((object) => object.name).reduce((prev, next) => '' + prev + ', ' + next);
                tooltipSpecificText = item[content].map((object) => '• ' + object.name).reduce((prev, next) => '' + prev + '\n' + next);
            }

        }
    } else if (type === 'object' && item[content] !== null && item[content] !== undefined) {
        text = item[content]['name'];
    } else if (type === 'text') {
        text = item[content];
        if (content === 'startDate' || content === 'endDate' || content === 'date' || content === 'lastActivityDate') {
            //date = [0], time = [1]
            let dateAndTime = text.split('T');
            let date = dateAndTime[0];
            text = date;
            
            if (content === 'endDate' && item.endDate !== null && item.endDate !== undefined){
                const currentWeek = WeekHelper.GetWeek(new Date());
                const endWeek = WeekHelper.GetWeek(item.endDate);
                if (WeekHelper.GetNumberOfWeeksBetween(currentWeek, endWeek) < WEEKS_BEFORE_ALERT){
                    textColor = 'text-danger';
                }
            }
        }
    }

    let resolvedLink = null;
    if (link && link.split('.').length > 1) {
        var fields = link.split('.');
        var finalObj = item;
        for (var i = 0; i < fields.length; i++) {
            if (finalObj[fields[i]] !== null)
                finalObj = finalObj[fields[i]];
        }
        if (finalObj && finalObj.length)
            resolvedLink = "/" + finalObj;
    }

    let resolvedContent = (<Text>{text}</Text>);
    if (tooltipSpecificText) {
        resolvedContent = (
            <Text tooltipStyle={{ whiteSpace: 'pre-wrap' }} tooltip={tooltipSpecificText}>
                {text}
            </Text>
        );
    }
    
    if (resolvedLink) {
        resolvedContent = (
            <a href={resolvedLink} target='_blank' rel='noopener noreferrer'>
                {resolvedContent}
            </a>
        );
    }

    return (
        <div
            className='d-flex align-items-center p-0 border-right border-bottom-0 cursor-default'
            onClick={() => {
                if (onClick !== null && !resolvedLink){
                    onClick(item);
                }
            }}
            style={{ height, width, minWidth: width, maxWidth: width, minHeight: height, maxHeight: height }}>
            <div key={item.id + '-' + content} className={`d-flex align-items-center justify-content-left pl-3 pr-3 w-100 ${textColor}`}>
                <>{resolvedContent}</>
            </div>
        </div>
    );
};
